import { graphql } from 'react-relay';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useLazyQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryInterop.ts';

import {
	agentGroupKey,
	getAutonomousAgentBrand,
	getUnknownAgentSingularName,
	isAgentPageView,
	isSelfAttributedAgentRecord,
	isUnidentifiedAgentGroupKey,
	resolveAgentNameAndIcon,
} from '../components/common/AgentActivity/agentClassification.ts';
import { toInjectedAgentTypePreviewPageViews } from '../components/common/AgentActivity/agentTypePreviewAgents.ts';
import { ActorType, getGatedPageAnalyticsActorTypeQueryValue } from '../store/storeTypes.ts';

import type { SitePermissionType } from './__generated__/usePageViewsAnalyticsViewersQuery.graphql.ts';
import type {
	usePageViewsAnalyticsViewersQuery as usePageViewsAnalyticsViewersQuery$data,
	usePageViewsAnalyticsViewersQueryVariables as usePageViewsAnalyticsViewersQuery$variables,
} from './__types__/usePageViewsAnalyticsViewersQuery.ts';
import type {
	usePageViewsAnalyticsViewersWithoutLimitQuery as usePageViewsAnalyticsViewersWithoutLimitQuery$data,
	usePageViewsAnalyticsViewersWithoutLimitQueryVariables as usePageViewsAnalyticsViewersWithoutLimitQuery$variables,
} from './__types__/usePageViewsAnalyticsViewersWithoutLimitQuery.ts';
import type {
	usePageViewsAnalyticsViewersWithActorTypeQuery$data,
	usePageViewsAnalyticsViewersWithActorTypeQuery$variables,
} from './__generated__/usePageViewsAnalyticsViewersWithActorTypeQuery.graphql.ts';
import type { Account, AnonymousAccount, AttributedUser, KnownAccount } from './accountTypes.ts';
import { AccountType } from './accountTypes.ts';

const usePageViewsAnalyticsViewersQuery = graphql`
	query usePageViewsAnalyticsViewersQuery(
		$contentId: ID!
		$limit: Int
		$engageTimeThreshold: Int
		$isPrivacyModeEnabled: Boolean
	) {
		singleContent(id: $contentId) {
			id
			subType
			version {
				number
			}
			contentAnalyticsViewsByUser(
				limit: $limit
				engageTimeThreshold: $engageTimeThreshold
				isPrivacyModeEnabled: $isPrivacyModeEnabled
			) {
				pageViews {
					userProfile {
						id
						displayName
						photos {
							value
							isPrimary
						}
						isActive
					}
					confluenceUser {
						permissionType
					}
					accountType
					agentType
					relatedUserAccountId
					relatedUser {
						id
						displayName
						photos {
							value
							isPrimary
						}
					}
					views
					lastViewedAt
					lastVersionViewedNumber
					lastVersionViewedUrl
					isEngaged
				}
			}
		}
	}
`;

const usePageViewsAnalyticsViewersWithoutLimitQuery = graphql`
	query usePageViewsAnalyticsViewersWithoutLimitQuery(
		$contentId: ID!
		$engageTimeThreshold: Int
		$isPrivacyModeEnabled: Boolean
	) {
		singleContent(id: $contentId) {
			id
			subType
			version {
				number
			}
			contentAnalyticsViewsByUser(
				engageTimeThreshold: $engageTimeThreshold
				isPrivacyModeEnabled: $isPrivacyModeEnabled
			) {
				pageViews {
					userProfile {
						id
						displayName
						photos {
							value
							isPrimary
						}
						isActive
					}
					confluenceUser {
						permissionType
					}
					accountType
					agentType
					relatedUserAccountId
					relatedUser {
						id
						displayName
						photos {
							value
							isPrimary
						}
					}
					views
					lastViewedAt
					lastVersionViewedNumber
					lastVersionViewedUrl
					isEngaged
				}
			}
		}
	}
`;

const usePageViewsAnalyticsViewersWithActorTypeQuery = graphql`
	query usePageViewsAnalyticsViewersWithActorTypeQuery(
		$contentId: ID!
		$limit: Int
		$engageTimeThreshold: Int
		$isPrivacyModeEnabled: Boolean
		$actorType: ConfluencePageAnalyticsActorType
	) {
		singleContent(id: $contentId) {
			id
			subType
			version {
				number
			}
		}
		contentAnalyticsViewsByUser(
			contentId: $contentId
			limit: $limit
			engageTimeThreshold: $engageTimeThreshold
			isPrivacyModeEnabled: $isPrivacyModeEnabled
			actorType: $actorType
		) {
			pageViews {
				userProfile {
					id
					displayName
					photos {
						value
						isPrimary
					}
					isActive
				}
				confluenceUser {
					permissionType
				}
				accountType
				agentType
				relatedUserAccountId
				relatedUser {
					id
					displayName
					photos {
						value
						isPrimary
					}
				}
				views
				lastViewedAt
				lastVersionViewedNumber
				lastVersionViewedUrl
				isEngaged
			}
		}
	}
`;

export type UsePageViewsOptions = {
	limit?: number;
	skip?: boolean;
	isPrivacyModeEnabled?: boolean;
	includeViewersActorType?: boolean;
	includeAgentTypePreview?: boolean;
};

export type UsePageViewsResult = {
	/** Content identity of the response, including while a different page is loading. */
	contentId?: string;
	error: ApolloErrorInterop | undefined;
	isLoading: boolean;
	viewers: Account[] | null;
	loadViewers: () => void;
	isLoadViewersCalled: boolean;
};

const viewerPermissionTypeToNonAnonymousAccountType: Partial<
	Record<SitePermissionType, KnownAccount['accountType']>
> = {
	APP: AccountType.NORMAL,
	EXTERNAL: AccountType.GUEST,
	INTERNAL: AccountType.NORMAL,
	JSD: AccountType.NORMAL,
};

type ViewerPageView = NonNullable<
	NonNullable<
		NonNullable<
			usePageViewsAnalyticsViewersQuery$data['singleContent']
		>['contentAnalyticsViewsByUser']
	>['pageViews']
>[number];

type LimitedQueryData =
	| usePageViewsAnalyticsViewersQuery$data
	| usePageViewsAnalyticsViewersWithActorTypeQuery$data;

const getPageViewsFromData = (
	data: LimitedQueryData | usePageViewsAnalyticsViewersWithoutLimitQuery$data | undefined | null,
	useRootActorTypeQuery: boolean,
): ViewerPageView[] => {
	if (!data) {
		return [];
	}
	if (useRootActorTypeQuery) {
		const pageViews =
			(data as usePageViewsAnalyticsViewersWithActorTypeQuery$data).contentAnalyticsViewsByUser
				?.pageViews ?? [];
		return [...pageViews];
	}
	const pageViews =
		(data as usePageViewsAnalyticsViewersQuery$data).singleContent?.contentAnalyticsViewsByUser
			?.pageViews ?? [];
	return [...pageViews];
};

export const usePageViews = (
	contentId: string,
	{
		limit,
		skip,
		isPrivacyModeEnabled = false,
		includeViewersActorType = false,
		includeAgentTypePreview = true,
	}: UsePageViewsOptions = {},
): UsePageViewsResult => {
	const { userId } = useSessionData();
	const { formatMessage } = useIntl();
	const hasLimit = limit != null;
	// Omitting $actorType on the WithActorType document still sends null; AGG rejects it.
	const actorTypeQueryValue =
		includeViewersActorType && hasLimit
			? getGatedPageAnalyticsActorTypeQueryValue(
					ActorType.All,
					expVal('ai_agent_analytics', 'isEnabled', false),
				)
			: undefined;
	const useRootActorTypeQuery = actorTypeQueryValue !== undefined;

	const limitedQuery = useRootActorTypeQuery
		? usePageViewsAnalyticsViewersWithActorTypeQuery
		: usePageViewsAnalyticsViewersQuery;

	const unlimitedQuery = usePageViewsAnalyticsViewersWithoutLimitQuery;

	const [
		fetchLimitedViewers,
		{ data: limitedData, loading: isLimitedLoading, error: limitedError, called: isLimitedCalled },
	] = useLazyQueryInterop<
		LimitedQueryData,
		| usePageViewsAnalyticsViewersQuery$variables
		| usePageViewsAnalyticsViewersWithActorTypeQuery$variables
	>(limitedQuery, {
		// network-only fetch policy added due to apparent caching issues where singleContent sometimes is set as null. Context: https://atlassian.slack.com/archives/C07GCFW40S3/p1742843738957579
		fetchPolicy: 'network-only',
	});

	const [
		fetchViewersWithoutLimit,
		{
			data: unlimitedData,
			loading: isUnlimitedLoading,
			error: unlimitedError,
			called: isUnlimitedCalled,
		},
	] = useLazyQueryInterop<
		usePageViewsAnalyticsViewersWithoutLimitQuery$data,
		usePageViewsAnalyticsViewersWithoutLimitQuery$variables
	>(unlimitedQuery, {
		// Skip Relay store read/write so Mentions lifetime viewers stay isolated from Viewers-table actor/date records.
		fetchPolicy: 'no-store',
	});

	const data = hasLimit ? limitedData : unlimitedData;
	const isLoading = hasLimit ? isLimitedLoading : isUnlimitedLoading;
	const pageViews = useMemo(() => {
		const views = getPageViewsFromData(data, useRootActorTypeQuery);
		if (!includeAgentTypePreview) {
			return views;
		}
		return [
			...toInjectedAgentTypePreviewPageViews(
				expVal('ai_analytics_agent_types', 'isEnabled', false),
				expVal('dual_agent_avatar_card', 'isEnabled', false),
			),
			...views,
		];
	}, [data, includeAgentTypePreview, useRootActorTypeQuery]);
	const error = hasLimit ? limitedError : unlimitedError;
	const isLoadViewersCalled = hasLimit ? isLimitedCalled : isUnlimitedCalled;

	// Permissions errors returned with status code 200
	if (
		(error?.message.includes('PermissionException') || error?.message.includes('403')) &&
		!isErrorMarkedAsHandled(error)
	) {
		markErrorAsHandled(error);
	}

	const loadViewers = useCallback(() => {
		if (skip) {
			return;
		}
		const variables = {
			contentId,
			engageTimeThreshold: 20,
			isPrivacyModeEnabled,
			...(actorTypeQueryValue !== undefined ? { actorType: actorTypeQueryValue } : {}),
		};

		if (limit != null) {
			// in case we need to exclude the current user we request 1 more than is needed.
			fetchLimitedViewers({
				variables: {
					...variables,
					limit: limit + 1,
				},
			});
			return;
		}

		fetchViewersWithoutLimit({ variables });
	}, [
		actorTypeQueryValue,
		contentId,
		fetchLimitedViewers,
		fetchViewersWithoutLimit,
		limit,
		skip,
		isPrivacyModeEnabled,
	]);

	const fetchedViewers: Account[] | null = useMemo(() => {
		if (!data) {
			return null;
		}

		// Maps a single human/anonymous page view to an Account (no agent grouping).
		const toHumanAccount = (pageView: ViewerPageView): Account => {
			if (!pageView.userProfile) {
				const anonymousAccount: AnonymousAccount = {
					id: null,
					name: null,
					avatarURL: null,
					accountType: AccountType.ANONYMOUS,
					lastViewedAt: pageView.lastViewedAt,
					isEngaged: pageView.isEngaged ?? null,
				};
				return anonymousAccount;
			}
			const permissionType = pageView.confluenceUser?.permissionType;
			const knownAccount: KnownAccount = {
				id: pageView.userProfile?.id ?? '',
				name: pageView.userProfile?.displayName ?? '',
				avatarURL: pageView.userProfile?.photos?.[0]?.value ?? '',
				lastViewedAt: pageView.lastViewedAt,
				accountType:
					(permissionType
						? viewerPermissionTypeToNonAnonymousAccountType[permissionType]
						: undefined) ?? AccountType.NORMAL,
				isDeactivated: !pageView.userProfile?.isActive,
				isEngaged: pageView.isEngaged ?? null,
			};
			return knownAccount;
		};

		// Agent UI is gated behind `ai_agent_analytics`. When the gate is off,
		// drop agent page views instead of mapping them through `toHumanAccount`
		// (which treats a missing `userProfile` as Anonymous). Real human
		// anonymous views still map as Anonymous.
		if (!expVal('ai_agent_analytics', 'isEnabled', false)) {
			const humanAccounts: Account[] = [];
			for (const pageView of pageViews) {
				if (!isAgentPageView(pageView)) {
					humanAccounts.push(toHumanAccount(pageView));
				}
			}
			return humanAccounts;
		}

		const agentGroups = new Map<string, ViewerPageView[]>();
		for (const pageView of pageViews) {
			if (!isAgentPageView(pageView)) {
				continue;
			}
			const key = agentGroupKey(pageView);
			const group = agentGroups.get(key);
			if (group) {
				group.push(pageView);
			} else {
				agentGroups.set(key, [pageView]);
			}
		}

		const buildAgentAccount = (record: ViewerPageView, groupKey: string): KnownAccount => {
			const groupRecords = agentGroups.get(groupKey) ?? [record];
			const resolvedName = groupRecords.find((r) => r.userProfile?.displayName)?.userProfile
				?.displayName;
			const {
				name: resolvedAgentName,
				agentIcon,
				avatarUrl,
			} = resolveAgentNameAndIcon(groupRecords, formatMessage);
			const resolvedAgentIcon = getAutonomousAgentBrand(record)?.agentIcon ?? agentIcon;
			const name = isUnidentifiedAgentGroupKey(groupKey)
				? getUnknownAgentSingularName(formatMessage)
				: resolvedAgentName;

			// Autonomous agent activity is reported against the agent's own account; it
			// has no attributed person, so it must not read as "with {the agent}".
			const relatedId = isSelfAttributedAgentRecord(record)
				? undefined
				: (record.relatedUserAccountId ?? record.relatedUser?.id);
			const relatedName = record.relatedUser?.displayName ?? '';
			const relatedAvatarURL =
				record.relatedUser?.photos?.find((photo) => photo?.isPrimary)?.value ??
				record.relatedUser?.photos?.find(Boolean)?.value;
			const attributedUser: AttributedUser | undefined = relatedId
				? {
						id: relatedId,
						name: relatedName,
						lastViewedAt: record.lastViewedAt,
						...(relatedAvatarURL ? { avatarURL: relatedAvatarURL } : {}),
					}
				: undefined;

			const identityAccountId = record.userProfile?.id;

			return {
				id: `${groupKey}${relatedId ? `:${relatedId}` : ''}`,
				name,
				avatarURL: avatarUrl ?? '',
				accountType: AccountType.NORMAL,
				lastViewedAt: record.lastViewedAt,
				isEngaged: null,
				isAgent: true,
				...(resolvedAgentIcon ? { agentIcon: resolvedAgentIcon } : {}),
				...(resolvedName && !isUnidentifiedAgentGroupKey(groupKey)
					? { isDeactivated: !groupRecords.find((r) => r.userProfile)?.userProfile?.isActive }
					: {}),
				...(attributedUser ? { attributedUsers: [attributedUser] } : {}),
				...(identityAccountId ? { identityAccountId } : {}),
			};
		};

		// One row per (agent, related user); autonomous agents get a single row.
		const emittedAgentRowKeys = new Set<string>();
		const accounts: Account[] = [];
		for (const pageView of pageViews) {
			if (!isAgentPageView(pageView)) {
				accounts.push(toHumanAccount(pageView));
				continue;
			}
			const key = agentGroupKey(pageView);
			// Must match `buildAgentAccount`'s attribution rule, or a self-attributed
			// record would be keyed as attributed but rendered as autonomous.
			const relatedId = isSelfAttributedAgentRecord(pageView)
				? undefined
				: (pageView.relatedUserAccountId ?? pageView.relatedUser?.id);
			if (isUnidentifiedAgentGroupKey(key) && !relatedId) {
				continue;
			}
			const rowKey = `${key}:${relatedId ?? ''}`;
			if (emittedAgentRowKeys.has(rowKey)) {
				continue;
			}
			emittedAgentRowKeys.add(rowKey);
			accounts.push(buildAgentAccount(pageView, key));
		}

		return accounts;
	}, [data, formatMessage, pageViews]);

	const viewers: Account[] | null = useMemo(() => {
		if (!fetchedViewers) {
			return null;
		}
		const filteredViewers = fetchedViewers.filter((account) => account.id !== userId);

		if (limit) {
			return filteredViewers.slice(0, limit);
		}

		return filteredViewers;
	}, [fetchedViewers, limit, userId]);

	return {
		contentId: data?.singleContent?.id ?? undefined,
		error,
		isLoading,
		viewers,
		loadViewers,
		isLoadViewersCalled,
	};
};
